import { addCalendarDays, type DeadlineKind } from "@sts/schemas";
import type { Deadline } from "../api/types.js";
import { days, isoDay } from "./format.js";

/**
 * What the compliance clocks are, and how close to the edge each one is.
 *
 * The API returns a due date and a day count; it does not say what the clock
 * is for or when it started. Both matter on screen: "12 days left" reads very
 * differently against a 30-day certificate window than against a 120-day
 * redemption window, and somebody looking at a breached deadline needs to know
 * which rule they have breached before they can do anything about it.
 */

export interface DeadlineRule {
  /** What to call it on screen. */
  readonly label: string;
  /** Where the obligation comes from, so the claim is checkable. */
  readonly authority: string;
  /** What the clock is measured from. */
  readonly measuredFrom: string;
  /**
   * Length of the window in calendar days, where it is fixed. Null where it is
   * not: a BUS voucher runs 3 months, or 6 for a ground source installation,
   * and the API does not tell us which — so the portal does not claim to know.
   */
  readonly windowDays: number | null;
}

export const DEADLINE_RULES: Record<DeadlineKind, DeadlineRule> = {
  "mcs-certificate": {
    label: "MCS certificate",
    authority: "MCS Installer Operating Requirements §4.10",
    measuredFrom: "commissioning",
    windowDays: 30,
  },
  "dno-notification": {
    label: "DNO notification",
    authority: "ENA connect and notify",
    measuredFrom: "installation",
    windowDays: 28,
  },
  // Deprecated kind, kept for rows started before the 2026-08-30 correction:
  // the 120-day window was mislabelled as "redemption" — it is actually the
  // Stage 1 voucher-application window (Installer Guidance V5.1 §3.13). New
  // timers use "bus-voucher-application" below; existing rows keep rendering.
  "bus-redemption": {
    label: "BUS redemption (legacy)",
    authority: "Boiler Upgrade Scheme",
    measuredFrom: "the commissioning date on the MCS certificate",
    windowDays: 120,
  },
  "bus-voucher-application": {
    label: "BUS voucher application",
    authority: "BUS Installer Guidance V5.1 §3.13 / Table A",
    measuredFrom: "the commissioning date on the MCS certificate",
    windowDays: 120,
  },
  "bus-voucher-expiry": {
    label: "BUS voucher expiry",
    authority: "Boiler Upgrade Scheme",
    measuredFrom: "voucher issue",
    windowDays: null,
  },
  "proposal-chase": {
    label: "Proposal chase",
    authority: "Internal",
    measuredFrom: "the proposal being sent",
    windowDays: null,
  },
};

/**
 * An unrecognised kind is possible: `DeadlineKind` is a database enum, and a
 * migration can add to it before this file learns about it. Showing the raw
 * value is honest; crashing the board is not.
 */
export function ruleFor(kind: DeadlineKind): DeadlineRule {
  return (
    DEADLINE_RULES[kind] ?? {
      label: kind,
      authority: "Unrecognised deadline kind",
      measuredFrom: "an unrecorded event",
      windowDays: null,
    }
  );
}

/**
 * How hard this is shouting.
 *
 * The bands are chosen against the tightest clock on the board. The MCS
 * certificate has to be registered within 30 days of commissioning, and
 * registering one means gathering commissioning evidence from an engineer who
 * is on another job — so a week is roughly the last point at which the office
 * can still fix it calmly, and two days is the point at which somebody has to
 * stop what they are doing.
 */
export type Urgency = "breached" | "critical" | "approaching" | "tracking";

export const URGENCY_ORDER: readonly Urgency[] = [
  "breached",
  "critical",
  "approaching",
  "tracking",
];

export function urgencyOf(daysRemaining: number): Urgency {
  if (daysRemaining < 0) return "breached";
  if (daysRemaining <= 2) return "critical";
  if (daysRemaining <= 7) return "approaching";
  return "tracking";
}

/** Heading for each band. Says what the band means, not just how bad it is. */
export const URGENCY_HEADING: Record<Urgency, string> = {
  breached: "Past the deadline",
  critical: "Due within two days",
  approaching: "Due within a week",
  tracking: "Tracking",
};

/**
 * The word that goes in the status column.
 *
 * Colour alone cannot carry this: some of the people who use compliance
 * software all day cannot distinguish the amber row from the red one, and a
 * printed board is often black and white.
 */
export const URGENCY_WORD: Record<Urgency, string> = {
  breached: "Overdue",
  critical: "Critical",
  approaching: "Due soon",
  tracking: "On track",
};

/** "8 days left", "due today", "3 days overdue". */
export function describeRemaining(daysRemaining: number): string {
  if (daysRemaining < 0) return `${days(-daysRemaining)} overdue`;
  if (daysRemaining === 0) return "due today";
  return `${days(daysRemaining)} left`;
}

/**
 * When the clock started, derived from the due date and the window length.
 *
 * Only for the kinds whose window is fixed. Guessing for the others would put
 * a date on screen that nothing in the system supports.
 */
export function startedOn(deadline: Deadline): string | null {
  const window = ruleFor(deadline.kind).windowDays;
  if (window === null) return null;
  return addCalendarDays(isoDay(deadline.due_at), -window);
}

export interface UrgencyGroup {
  readonly urgency: Urgency;
  readonly deadlines: readonly Deadline[];
}

/**
 * Groups the board by band, keeping the API's ordering within each.
 *
 * The API already sorts by due date ascending, which is the order that matters;
 * this only splits it, so a re-sort here could only disagree with the server.
 */
export function groupByUrgency(
  deadlines: readonly Deadline[],
): readonly UrgencyGroup[] {
  return URGENCY_ORDER.map((urgency) => ({
    urgency,
    deadlines: deadlines.filter((d) => urgencyOf(d.days_remaining) === urgency),
  })).filter((group) => group.deadlines.length > 0);
}

/** One line for the top of the board and for the page's live region. */
export function summarise(deadlines: readonly Deadline[]): string {
  if (deadlines.length === 0) return "Nothing outstanding.";
  const counts = new Map<Urgency, number>();
  for (const deadline of deadlines) {
    const urgency = urgencyOf(deadline.days_remaining);
    counts.set(urgency, (counts.get(urgency) ?? 0) + 1);
  }
  const parts = URGENCY_ORDER.filter((u) => counts.has(u)).map(
    (u) => `${counts.get(u)!} ${URGENCY_WORD[u].toLowerCase()}`,
  );
  return `${deadlines.length} outstanding: ${parts.join(", ")}.`;
}

/** The worst band present, for badging a job in the pipeline. */
export function worstUrgency(
  deadlines: readonly Deadline[],
): Urgency | null {
  let worst: Urgency | null = null;
  for (const deadline of deadlines) {
    const urgency = urgencyOf(deadline.days_remaining);
    if (
      worst === null ||
      URGENCY_ORDER.indexOf(urgency) < URGENCY_ORDER.indexOf(worst)
    ) {
      worst = urgency;
    }
  }
  return worst;
}
